import type { TFunction } from "i18next";
import { DomainTopics, MARKETPLACE_ADMIN_EVENT_TYPES } from "@nexus/contracts";
import { eventTypeLabel } from "../lib/event-type-label";
import type { EventJobStatus } from "@/features/events/api/events.api";
import { eventTopicLabel } from "../lib/event-topic-label";

export const EVENT_FILTER_ALL = "all";

export interface EventFilterOption {
  value: string;
  label: string;
  photoUrl?: string | null;
}

const EVENT_STATUSES: readonly EventJobStatus[] = [
  "PENDING",
  "PROCESSING",
  "COMPLETED",
  "FAILED",
] as const;

const STATUS_LABEL_FALLBACK: Record<EventJobStatus, string> = {
  PENDING: "Pending",
  PROCESSING: "Processing",
  COMPLETED: "Completed",
  FAILED: "Failed",
};

/**
 * Option labels are user-facing, and module scope cannot call `useTranslation`,
 * so these take `t` from the caller (see the `translateBrandColorLabel` pattern).
 */
export function buildEventStatusOptions(t: TFunction): EventFilterOption[] {
  return EVENT_STATUSES.map((status) => ({
    value: status,
    label: t(`admin.events.status.${status}`, STATUS_LABEL_FALLBACK[status]),
  }));
}

export function buildEventOutcomeOptions(t: TFunction): EventFilterOption[] {
  return [
    { value: EVENT_FILTER_ALL, label: t("admin.events.filters.outcome", "All outcomes") },
    { value: "success", label: t("admin.events.outcome.success", "Success") },
    { value: "failed", label: t("admin.events.outcome.failed", "Failed") },
    { value: "skipped", label: t("admin.events.outcome.skipped", "Skipped") },
  ];
}

export function buildEventSubjectKindOptions(t: TFunction): EventFilterOption[] {
  return [
    { value: EVENT_FILTER_ALL, label: t("admin.events.filters.subjectKind", "All subjects") },
    { value: "order", label: t("admin.events.subject.order", "Order") },
    { value: "script", label: t("admin.events.subject.script", "Script") },
    { value: "scriptReview", label: t("admin.events.subject.scriptReview", "Script review") },
    { value: "submission", label: t("admin.events.subject.submission", "Submission") },
    { value: "vendor", label: t("admin.events.subject.vendor", "Partner") },
    { value: "import", label: t("admin.events.subject.import", "Import") },
    { value: "email", label: t("admin.events.subject.email", "Email") },
  ];
}

export function buildMarketplaceEventTypeOptions(t: TFunction): EventFilterOption[] {
  return MARKETPLACE_ADMIN_EVENT_TYPES.map((eventType) => ({
    value: eventType,
    label: eventTypeLabel(t, eventType),
  }));
}

export function buildEventTopicOptions(t: TFunction): EventFilterOption[] {
  return Object.values(DomainTopics).map((topic) => ({
    value: topic,
    label: eventTopicLabel(t, topic),
  }));
}
